import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import RewardModal from './RewardModal';
import { shiftMany } from '../../spin/store/rewardsQueueSlice';
import { flyRewardToTarget } from '../../../shared/utils/rewardAnimation';
import { fetchWalletBalance } from '../../wallet/store/walletSlice';
import { useAudio } from '../../../contexts/AudioContext';
import { useWebSocket } from '../../wallet/hooks/useWebSocket';
import { addNewInventoryItems } from '../../inventory/store/inventoryNewSlice';

export default function RewardCenter() {
  const audioRef = useAudio();
  const dispatch = useDispatch();
  const queue = useSelector((s) => s.rewardsQueue.queue);
  const [current, setCurrent] = useState(null);
  const [mergedCount, setMergedCount] = useState(1);

  // Получаем userId из localStorage для WebSocket подключения
  const userId = useSelector((s) => s.auth?.user?.id) || localStorage.getItem('userId');
  const { socket, isConnected, reconnect } = useWebSocket(userId);

  useEffect(() => {
    if (!current && queue.length > 0) {
      // Merge consecutive items with the same type+label into one modal
      const first = queue[0];
      const key = first.type + '_' + first.label;
      let merged = { ...first };
      let count = 1;
      while (count < queue.length && queue[count].type + '_' + queue[count].label === key) {
        merged = {
          ...merged,
          value: (Number(merged.value) || 0) + (Number(queue[count].value) || 0),
        };
        count++;
      }
      setMergedCount(count);
      setCurrent(merged);
    }
  }, [queue, current]);

  const handleClose = () => {
    setCurrent(null);
    dispatch(shiftMany(mergedCount));
    setMergedCount(1);
  };

  // Secondary button: same confirm flow (fly animation + close), then notify the
  // screen that asked for it. The queue holds only serializable data, so the action
  // is a DOM event name + detail instead of a callback.
  const handleSecondary = () => {
    const action = current?.secondaryAction;
    handleConfirm();
    if (action?.event) {
      setTimeout(() => window.dispatchEvent(new CustomEvent(action.event, { detail: action.detail })), 150);
    }
  };

  const handleConfirm = () => {
    // Получаем стартовый rect и src для анимации перед закрытием модалки
    const imgEl = document.querySelector('.modal-reward-image');
    if (!imgEl) {
      console.warn('RewardCenter: modal-reward-image not found');
      handleClose();
      return;
    }
    
    const startRect = imgEl.getBoundingClientRect();
    const iconSrc = imgEl.getAttribute('src');
    const target = current?.animationTarget || 'coins';
    const sound = current?.sound || 'reward';
    
    console.log('🎁 RewardCenter: Starting animation', { iconSrc, target, startRect, sound });
    
    // Запрашиваем обновление баланса и счетчиков через WebSocket
    // Бэкенд не отправлял события при спине, теперь запрашиваем при подтверждении
    if (socket && isConnected) {
      console.log('📡 Requesting balance and counters update via WebSocket', { socketId: socket.id, connected: isConnected });
      socket.emit('getWalletBalance');
      socket.emit('counters.request');
    } else {
      console.warn('⚠️ WebSocket not available or not connected', { socket: socket, isConnected: isConnected });
      // Пытаемся переподключиться, если сокет не доступен
      if (!socket && userId && reconnect) {
        console.log('🔄 Attempting to reconnect WebSocket...');
        reconnect();
        // После переподключения запросы отправятся автоматически при следующем подтверждении
      }
    }
    
    // Запускаем анимацию
    flyRewardToTarget({ iconSrc, target, startRect });

    // Обновляем баланс когда анимация долетает до цели (~1s).
    // Работает независимо от состояния WebSocket.
    setTimeout(() => {
      dispatch(fetchWalletBalance());
    }, 1000);

    // Предмет (часть героя / сундук / ключ) долетел до меню Inventory → +N к бейджу новых.
    // value у сундука/ключа = количество; у склеенных одинаковых модулей — сумма, но может быть 0.
    if (target === 'inventory') {
      const count = Math.max(mergedCount, Number(current?.value) || 0);
      setTimeout(() => dispatch(addNewInventoryItems(count)), 1400);
    }

    // Небольшая задержка перед закрытием, чтобы анимация успела стартовать
    setTimeout(() => {
      handleClose();
    }, 100);
  };

  return <>{current && <RewardModal reward={current} onClose={handleClose} onConfirm={handleConfirm} onSecondary={handleSecondary} />}</>;
}


